/**
 * Pure derivations for the chat layout: composer placeholder, suggested
 * prompts and the shell's "recent" sidebar items.
 */
import type { UserDocumentItem } from '../../services/api';
import type { ChatSession } from '../../types';
import { formatDateTime } from './chatFormat';
import type { TFn } from './chatFlowTypes';

/** Composer placeholder, most specific state first. */
export function composerPlaceholder(
  t: TFn,
  state: {
    dragOver: boolean;
    chatLocked: boolean;
    reassessOnly: boolean;
    isTrialExhausted: boolean;
    analyzingFile: boolean;
    paidChatLimitExceeded: boolean;
  },
): string {
  const { dragOver, chatLocked, reassessOnly, isTrialExhausted, analyzingFile, paidChatLimitExceeded } = state;
  return dragOver
    ? t('chat.dropToAttach')
    : chatLocked
      ? t('entitlementLocks.chatPlaceholder')
      : reassessOnly
      ? t('entitlementLocks.chatReassessPlaceholder')
      : isTrialExhausted
      ? t('trial.chatLimit.inputPlaceholder')
      : analyzingFile
        ? t('chat.analysisInProgress')
        : paidChatLimitExceeded
          ? t('chat.limitExceededPlaceholder')
          : t('chat.webComposerPlaceholder');
}

/**
 * Suggested prompts under the composer. Re-assess-only (D3): the usual
 * prompts are plain chat, which would be refused, so offer the credit-billed
 * re-assessments instead (the marker follow-up needs a tier2+ plan, like on
 * the server).
 */
export function chatSuggestedPrompts(
  t: TFn,
  { reassessOnly, allowMarkerTable }: { reassessOnly: boolean; allowMarkerTable: boolean },
): string[] {
  return reassessOnly
    ? [
      ...(allowMarkerTable ? [t('entitlementLocks.reassessSuggestionMarkers')] : []),
      t('entitlementLocks.reassessSuggestionReanalyze'),
    ]
    : [
      t('chat.webSuggestionStrength'),
      t('chat.webSuggestionImprove'),
      t('chat.webSuggestionPcc'),
    ];
}

export interface ChatRecentItem {
  id: string | number;
  title: string;
  subtitle: string;
  active: boolean;
  onPress: () => void;
}

/**
 * The shell's recent items: the four latest documents, or the four latest
 * sessions when there are no documents.
 */
export function buildChatRecentItems({
  t,
  recentDocuments,
  sessions,
  currentSessionId,
  getSessionDisplayTitle,
  onOpenDocument,
  onOpenSession,
}: {
  t: TFn;
  recentDocuments: UserDocumentItem[];
  sessions: ChatSession[];
  currentSessionId: number | null;
  getSessionDisplayTitle: (session?: ChatSession | null) => string;
  onOpenDocument: (item: UserDocumentItem) => void;
  onOpenSession: (sessionId: number) => void;
}): ChatRecentItem[] {
  const recentDocumentItems = recentDocuments.slice(0, 4).map((item) => ({
    id: `document-${item.id}`,
    title: item.original_filename,
    subtitle: item.document_type === 'user_transcript'
      ? t('library.typeVoice')
      : item.document_type === 'user_material'
        ? t('library.filterMaterials')
        : t('library.typeText'),
    active: Boolean(item.session_id && item.session_id === currentSessionId),
    onPress: () => onOpenDocument(item),
  }));
  const sessionRecentItems = sessions.slice(0, 4).map((session) => ({
    id: session.id,
    title: getSessionDisplayTitle(session),
    subtitle: formatDateTime(session.created_at),
    active: session.id === currentSessionId,
    onPress: () => onOpenSession(session.id),
  }));
  return recentDocumentItems.length > 0 ? recentDocumentItems : sessionRecentItems;
}
